import angular from 'angular';
import template from './templates/tour.tpl.html';
import debounce from 'lodash.debounce';

const KEYS = [
  'components.tour.label.back',
  'components.tour.label.next',
  'components.tour.label.done',
  'components.tour.label.noStepMessage'
];

const [PLACEMENT, CLASS_PREFIX, APPEND_TO_BODY, SCROLL_SPEED, STEP_COUNT, ARROW_MARGIN,
  TOUR_HEADER] = ['bottom', 'tour-active-', false, 500, 1, 14, 'components.tour.header'];

const PLACEMENTS = ['top', 'left', 'bottom', 'right'];

const translate = (SERVICE, key) => SERVICE.$translate(key);
const assignLabelValues = (ctrl, values) => {
  const [backLabel, nextLabel, doneLabel, noStepMessage] = values;
  ctrl.backLabel = backLabel;
  ctrl.nextLabel = nextLabel;
  ctrl.doneLabel = doneLabel;
  ctrl.noStepMessage = noStepMessage;
};

const [FORWARD, BACKWARD] = ['forward', 'backward'];

class TourController {
  constructor(...args) {
    Object.assign(this, {
      SERVICE: args[0]
    });
    const translateFn = translate.bind(null, this.SERVICE);
    const assignLabelValuesFn = assignLabelValues.bind(null, this);
    this.initialize(translateFn, assignLabelValuesFn);
  }

  initialize(translateFn, resultFn) {
    this.SERVICE.$q.all(KEYS.map(translateFn)).then(values => resultFn(values));
    this.removed = false;
    this.totalSteps = this.SERVICE.steps.size;
    this.setStepNumber();
  }

  cancel() {
    this.SERVICE.cancelTour();
  }

  next() {
    this.SERVICE.move();
    this.setStepNumber();
  }

  back() {
    this.SERVICE.move(BACKWARD);
    this.setStepNumber();
  }

  remove(e) {
    this.SERVICE.removeTour(this.removed);
    e.stopPropagation();
  }

  setStepNumber() {
    this.currentStepNumber = (this.SERVICE.stepIndex || 0) + STEP_COUNT;
  }
}

class TourService {
  constructor(...args) {
    Object.assign(this, {
      $document: args[0],
      $compile: args[1],
      $rootScope: args[2],
      $timeout: args[3],
      $uibPosition: args[4],
      $log: args[5],
      $translate: args[6],
      $q: args[7],
      $window: args[8]
    });

    this.positionTimeout = undefined;
    this.tourtip = null;
    this.currentStepIndex = 0;
    this.containerEl = angular.element(this.$document).find('body');

    this.$rootScope.$on('$destroy', () => {
      if (this.tourScope) {
        this.tourScope.$destroy();
        this.tourScope = null;
      }
    });

    this.steps = new Set();
  }

  show() {
    angular.element(this.$window).bind(
      `resize.${this.tourScope.$id}`,
      debounce(this.positionTourtip, 50)
    );
    this.tourScope.$evalAsync(() => {
      this.positionTourtip();
      this.onDisplay(this.currentStep);
    });
  }

  get currentStep() {
    return [...this.steps][this.stepIndex];
  }

  get stepIndex() {
    return this.currentStepIndex;
  }

  set stepIndex(index) {
    this.currentStepIndex = index;
  }

  get notShowTourAgain() {
    return this.noNeedToShowAgain;
  }

  set notShowTourAgain(notToShow) {
    this.noNeedToShowAgain = notToShow;
  }

  removeTour(toRemove) {
    this.onRemove(toRemove);
    //this flag will be used in cancel call for closing the tour
    this.notShowTourAgain = toRemove;
  }

  move(direction = FORWARD) {
    this.clearCss();
    this.stepIndex = direction === BACKWARD ?
      this.stepIndex - STEP_COUNT : this.stepIndex + STEP_COUNT;
    this.tourScope.tour.activeStep = this.currentStep;
    this.show();
  }

  getElement(selector) {
    return angular.element(this.$document[0].querySelector(selector));
  }

  positionTourtip() {
    const element = this.getElement(this.currentStep.selector);

    if (!this.hideBackdrop) {
      this.containerEl.append('<div class="tour-backdrop"></div>');
    }
    // tourtip element has to be added to DOM before CSS update,
    // so it can properly calculate offsetHeight and offsetTop
    element
      .addClass(`${CLASS_PREFIX}element`)
      .after(this.tourtip);

    this.tourtip.addClass('fade');

    if (!this.positionTimeout) {
      this.positionTimeout = this.$timeout(() => {

        const positionCss = TourService.getPositionCss(
          this.tourtip,
          element,
          this.currentStep.placement,
          this.$uibPosition
        );

        this.tourtip
          .css(positionCss)
          .addClass('in');

        TourService.scrollTo(
          this.tourtip,
          this.$uibPosition.offset(element).top,
          this.containerEl[0].offsetHeight / 3
        );

        this.positionTimeout = null;
      });
    }
  }

  prepareTour(tour) {
    const valid = TourService.validateTourData(tour);

    if (!valid) {
      this.$log.error('Tour has failed to start due to invalid data.');
      return;
    }

    //keep original tour object
    this.origTour = angular.copy(tour);

    this.tourScope.title = tour.title || TOUR_HEADER;
    this.tourScope.titleValues = tour.titleValues;
    this.hideBackdrop = !!tour.hideBackdrop;
    this.onDisplay = tour.onDisplay || angular.noop;
    this.onRemove = tour.onRemove || angular.noop;

    //set step data
    this.buildTourSteps();
    this.tourScope.tour = new TourController(this);
    this.tourScope.tour.activeStep = this.currentStep;
  }

  addStep(step) {
    const elem = this.getElement(step.selector);

    if (TourService.validateStepData(step, elem)) {
      step.placement = step.placement && PLACEMENTS.find(x => x === step.placement.toLowerCase())
      || PLACEMENT;
      step.title = step.title || this.tourScope.title;
      step.titleValues = step.titleValues || this.tourScope.titleValues;
      this.steps.add(step);
    } else {
      this.$log.warn('This step is invalid: ', step);
    }
  }

  buildTourSteps() {
    Array.from(this.origTour.steps).forEach(this.addStep, this);
  }

  cancelTour() {
    this.tourScope.$emit('tour.tourCanceled');

    // scroll back to top before remove
    TourService.scrollTo(this.tourtip, 0, 0);

    //need to check half way canceled or tour complete finished
    if (this.tourtip) {
      this.clearCss();
      this.tourtip.remove();
      this.tourtip = null;
    }

    //may not need, extra clean up
    angular.element(this.getElement('.tour-active-tip')).remove();
    //if it is not last step, return
    if (this.stepIndex + 1 < this.steps.size && !this.notShowTourAgain) {
      return;
    }

    this.stepIndex = 0;
    this.steps.clear();
    this.onRemove(true);
    this.tourScope.$destroy();
    this.notShowTourAgain = true;
  }

  clearCss() {
    if (!this.hideBackdrop) {
      angular.element(this.$document[0].querySelectorAll('.tour-backdrop')).remove();
    }
    angular.element(this.getElement(this.currentStep.selector))
      .removeClass('tour-active-element');
    this.tourtip.removeClass('in');
  }

  /**
   * start an entry point to launch a tour
   * @param  {Object} tour that contains information to be parsed and used to launch
   */
  start(tour) {
    if (!this.tourScope) {
      this.tourScope = this.tourScope || this.$rootScope.$new(true);
      this.prepareTour(tour);
    }
    this.tourtip = this.$compile(template)(this.tourScope);
    this.tourtip.addClass(`${CLASS_PREFIX}tip`);
    this.notShowTourAgain = false;
    this.show();

    this.tourScope.$emit('tour.tourStarted');

    this.tourScope.$on('$destroy', () => {
      if (this.tourScope && this.tourScope.$id) {
        angular.element(this.$window).unbind(`resize.${this.tourScope.$id}`);
      }
      this.tourScope = null;
    });
  }

  static scrollTo(tourtip, targetElTop, containerDisplayTop) {
    const offsetX = 0;

    if (tourtip) {
      const offsetY = targetElTop > containerDisplayTop ? targetElTop - 200 : 0;
      window.scrollTo(offsetX, offsetY, SCROLL_SPEED);
    }
  }

  static getPositionCss(tourtip, element, placement, $position) {
    // Reset the positioning.
    tourtip.css({
      top: 0,
      left: 0
    });

    const tourtipCss = $position.positionElements(
      element,
      tourtip,
      placement,
      APPEND_TO_BODY
    );

    //following is not ideal as hard coded value for arrow pointer,
    //we may need to dynamically calculate
    let marginTop = 0,
      marginLeft = 0;
    switch (placement) {
      case 'top':
        marginTop = -ARROW_MARGIN;
        break;
      case 'bottom':
        marginTop = ARROW_MARGIN;
        break;
      case 'left':
        marginLeft = -ARROW_MARGIN;
        break;
      case 'right':
        marginLeft = ARROW_MARGIN;
    }

    tourtipCss.top += 'px';
    tourtipCss.left += 'px';
    tourtipCss.marginTop = `${marginTop}px`;
    tourtipCss.marginLeft = `${marginLeft}px`;

    return tourtipCss;
  }

  static validateStepData(step, elem) {
    return Object.keys(step).length && Object.keys(elem).length && step.content.trim();
  }

  static validateTourData(tour) {
    if (!angular.isObject(tour)) {
      throw new Error('A valid tour object is required to start tour');
    }
    return !Array.isArray(tour.steps) || tour.steps.length > 3;
  }
}

function tourService($document, $compile, $rootScope, $timeout,
  $uibPosition, $log, $translate, $q, $window) {
  return new TourService($document, $compile, $rootScope, $timeout,
    $uibPosition, $log, $translate, $q, $window);
}
tourService.$inject = ['$document', '$compile', '$rootScope', '$timeout',
  '$uibPosition', '$log', '$translate', '$q', '$window'
];

export default tourService;
